import { zodResolver } from '@hookform/resolvers/zod'
import * as Linking from 'expo-linking'
import { useRouter } from 'expo-router'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { ScrollView, TextInput, View } from 'react-native'
import { StyleSheet } from 'react-native-unistyles'
import { z } from 'zod'

import FormFieldText from '@/components/form/FormFieldText'
import Logo from '@/components/navigation/Logo'
import Snackbar from '@/components/snackbar/Snackbar'
import UniButton from '@/components/unistyles/UniButton'
import UniText from '@/components/unistyles/UniText'
import { useAppStore } from '@/store/useAppStore'
import {
  clearLoginAttemptInfo,
  doesSessionExist,
  getCode,
  getLoginAttemptInfo,
  passwordSignIn,
  validateOtp,
} from '@/utils/auth/auth'
import log from '@/utils/datadog/log/log'
import { logError } from '@/utils/log'

const REVIEWER_EMAILS = [
  'appstore@rescuebase.com',
  'playstore@rescuebase.com',
  'justinvdhooft+e2e@gmail.com',
]

const loginFormSchema = z.discriminatedUnion('state', [
  z.object({
    state: z.literal('email'),
    email: z.string().email('Please enter a valid email address'),
    password: z.string().optional(),
    otp: z.string().optional(),
  }),
  z.object({
    state: z.literal('otp'),
    email: z.string().optional(),
    password: z.string().optional(),
    otp: z.string(),
  }),
])

type LoginForm = z.infer<typeof loginFormSchema>

const Login = () => {
  const [showPasswordAuth, setShowPasswordAuth] = useState(false)
  const [otpError, setOtpError] = useState('')
  const [isSigningIn, setIsSigningIn] = useState(false)
  const [isValidatingOtp, setIsValidatingOtp] = useState(false)
  const [currentPage, setCurrentPage] = useState<'email' | 'otp'>('email')

  const router = useRouter()
  const setAuth = useAppStore.use.setAuth()
  const otpInputRef = useRef<TextInput>(null)

  const {
    control,
    handleSubmit,
    getValues,
    reset,
    setError,
    watch,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginFormSchema),
    defaultValues: { state: 'email', email: '', password: '', otp: '' },
  })

  const email = watch('email')

  const handleAuth = async (success: boolean) => {
    if (success) {
      setAuth({ accessToken: undefined, isLoggedIn: true })
      await clearLoginAttemptInfo()
      router.replace('/')
    }
  }

  const onSubmitEmail = handleSubmit(async () => {
    setIsSigningIn(true)
    try {
      const { email, password } = getValues()

      if (!email) {
        Snackbar.error('Please enter your email')
        return
      }

      if (REVIEWER_EMAILS.includes(email.toLowerCase())) {
        if (!password) {
          setError('password', { message: 'Please enter your password' })
          return
        }
        const response = await passwordSignIn(email, password)
        if (response.status === 'WRONG_CREDENTIALS_ERROR') {
          Snackbar.error('Email password combination is incorrect.')
        } else {
          await handleAuth(
            response.status !== 'FIELD_ERROR' &&
              response.status !== 'SIGN_IN_NOT_ALLOWED'
          )
        }
      } else {
        const response = await getCode(email)
        if (response.status !== 'SIGN_IN_UP_NOT_ALLOWED') {
          setCurrentPage('otp')
          reset({ state: 'otp', email, password: '', otp: '' })
        }
      }
    } catch (err: any) {
      log.error(err.message, err)
      Snackbar.error('Oops! Something went wrong.')
    } finally {
      setIsSigningIn(false)
    }
  })

  const onSubmitOtp = handleSubmit(
    async () => {
      setIsValidatingOtp(true)
      try {
        const { otp } = getValues()
        if (!otp) {
          setOtpError('Please enter the code sent to your email')
          return
        }

        const response = await validateOtp(otp)
        switch (response.status) {
          case 'OK':
            await handleAuth(true)
            break
          case 'INCORRECT_USER_INPUT_CODE_ERROR':
            setOtpError('The code you entered is invalid.')
            break
          case 'EXPIRED_USER_INPUT_CODE_ERROR':
            setOtpError('The code you entered is expired. Please log in again.')
            break
          default:
            Snackbar.error('Login failed. Please try again')
            await handleAuth(false)
        }
      } catch (err: any) {
        log.error(err.message, err)
        Snackbar.error('Oops! Something went wrong.')
      } finally {
        setIsValidatingOtp(false)
      }
    },
    (err) => {
      console.log('err ', err)
    }
  )

  const goBackToEmail = () => {
    setCurrentPage('email')
    clearLoginAttemptInfo().catch(logError)
  }

  useEffect(() => {
    doesSessionExist().then((exists) => {
      if (exists) {
        setAuth({ accessToken: undefined, isLoggedIn: true })
        router.replace('/')
      }
    })
  }, [router, setAuth])

  useEffect(() => {
    getLoginAttemptInfo()
      .then((info) => {
        if (info) {
          setCurrentPage('otp')
          reset({
            state: 'otp',
            email: getValues('email'),
            password: '',
            otp: '',
          })
        }
      })
      .catch(logError)
  }, [])

  useEffect(() => {
    setShowPasswordAuth(REVIEWER_EMAILS.includes((email || '').toLowerCase()))
  }, [email])

  useEffect(() => {
    if (currentPage === 'otp') {
      // Focus the OTP input after a short delay to ensure the component has rendered
      const timer = setTimeout(() => {
        otpInputRef.current?.focus()
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [currentPage])

  const renderEmailPage = () => (
    <View style={styles.formContent}>
      <UniText style={styles.heading} variant="headlineMedium">
        Sign In
      </UniText>

      <FormFieldText
        accessibilityHint="Enter your email address"
        accessibilityLabel="Email Address"
        autoCapitalize="none"
        autoComplete="email"
        control={control}
        errors={errors}
        fieldName="email"
        id="magic-link-input" // HACK: Works around this issue https://github.com/mobile-dev-inc/Maestro/issues/2386
        inputMode="email"
        label="Email Address"
        onSubmitEditing={onSubmitEmail}
        required={true}
        returnKeyType="go"
        testID="magic-link-input"
        textContentType="emailAddress"
      />

      {showPasswordAuth && (
        <FormFieldText
          accessibilityHint="Enter your password"
          accessibilityLabel="Password"
          control={control}
          errors={errors}
          fieldName="password"
          id="password-input" // HACK: Works around this issue https://github.com/mobile-dev-inc/Maestro/issues/2386
          label="Password"
          onSubmitEditing={onSubmitEmail}
          required={true}
          returnKeyType="go"
          secureTextEntry
          style={styles.passwordField}
          testID="password-input"
        />
      )}

      <UniButton
        disabled={isSigningIn}
        loading={isSigningIn}
        mode="contained"
        onPress={onSubmitEmail}
        style={styles.submitButton}
        testID={
          showPasswordAuth ? 'password-login-button' : 'magic-link-button'
        }
      >
        {showPasswordAuth ? 'Login' : 'Send magic link'}
      </UniButton>
    </View>
  )

  const renderOtpPage = () => (
    <View style={styles.formContent}>
      <UniText style={styles.otpDescription} variant="titleMedium">
        Check your email for a login code.
      </UniText>

      {otpError ? (
        <UniText
          style={styles.errorText}
          testID="otp-error-message"
          variant="bodyMedium"
        >
          {otpError}
        </UniText>
      ) : null}

      <FormFieldText
        accessibilityHint="Enter the login code sent to your email"
        accessibilityLabel="Login Code"
        control={control}
        fieldName="otp"
        inputMode="numeric"
        label="Enter your login code"
        onSubmitEditing={onSubmitOtp}
        ref={otpInputRef}
        required={true}
        returnKeyType="go"
        testID="otp-input"
        textContentType="oneTimeCode"
      />

      <UniButton
        disabled={isValidatingOtp}
        loading={isValidatingOtp}
        mode="contained"
        onPress={onSubmitOtp}
        style={styles.submitButton}
        testID="otp-button"
      >
        Sign in
      </UniButton>

      <UniButton
        mode="outlined"
        onPress={goBackToEmail}
        style={styles.backButton}
        testID="otp-back-button"
      >
        Back
      </UniButton>
    </View>
  )

  return (
    <ScrollView
      contentContainerStyle={styles.scrollContent}
      keyboardShouldPersistTaps="handled"
      style={styles.scrollView}
    >
      <View style={styles.cardContainer}>
        <View style={styles.logoContainer}>
          <Logo style={{}} />
        </View>
        <View style={styles.card}>
          <View style={styles.cardContent}>
            {currentPage === 'email' ? renderEmailPage() : renderOtpPage()}
          </View>
        </View>
      </View>

      <UniText allowFontScaling={false} style={styles.legalText}>
        By continuing, you agree to our{' '}
        <UniText
          allowFontScaling={false}
          onPress={() =>
            Linking.openURL('https://www.rescuebase.com/terms.html')
          }
          style={styles.legalLink}
        >
          Terms of Service
        </UniText>{' '}
        and{' '}
        <UniText
          allowFontScaling={false}
          onPress={() =>
            Linking.openURL('https://www.rescuebase.com/privacy.html')
          }
          style={styles.legalLink}
        >
          Privacy Policy
        </UniText>
      </UniText>
    </ScrollView>
  )
}

const styles = StyleSheet.create((theme) => ({
  backButton: {
    marginTop: theme.tokens.spacing[2],
  },
  card: {
    backgroundColor: theme.colors.surface,
    borderColor: theme.colors.cardBorder,
    borderRadius: theme.tokens.containerBorderRadius * 2,
    borderWidth: 1,
  },
  cardContainer: {
    alignSelf: 'center',
    flex: 1,
    justifyContent: 'center',
    maxWidth: 450,
    width: '100%',
  },
  cardContent: {
    padding: theme.tokens.spacing[6],
  },
  errorText: {
    backgroundColor: theme.colors.errorContainer,
    borderRadius: theme.tokens.containerBorderRadius,
    color: theme.colors.error,
    padding: theme.tokens.spacing[3],
    textAlign: 'center',
  },
  formContent: {
    gap: theme.tokens.spacing[4],
  },
  heading: {
    color: theme.colors.primary,
    marginBottom: theme.tokens.spacing[2],
    textAlign: 'center',
  },
  legalLink: {
    color: theme.colors.primary,
    textDecorationLine: 'underline',
  },
  legalText: {
    color: theme.colors.onSurfaceVariant,
    lineHeight: 20,
    paddingHorizontal: theme.tokens.spacing[4],
    textAlign: 'center',
  },
  logoContainer: {
    alignItems: 'center',
    paddingBottom: theme.tokens.spacing[6],
  },
  otpDescription: {
    color: theme.colors.onSurfaceVariant,
    lineHeight: 22,
    textAlign: 'center',
  },
  passwordField: {
    marginTop: theme.tokens.spacing[2],
  },
  scrollContent: {
    flex: 1,
    padding: theme.tokens.spacing[4],
    paddingBottom: theme.tokens.spacing[8],
    paddingTop: theme.tokens.spacing[8],
  },
  scrollView: {
    backgroundColor: theme.colors.background,
    flex: 1,
  },
  submitButton: {
    marginTop: theme.tokens.spacing[4],
  },
}))

export default Login
